/**
 * Phase 6b — Executive Alert Analytics
 * Permission: EXEC_ALERT_ANALYTICS_LIHAT (super_admin, direktur_*, komisaris, auditor).
 * Read-only: alert trend, recurrence, threshold review suggestion.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Activity, AlertTriangle, Repeat, Timer, History } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

const CLASS_COLOR = {
  persistent: "bg-rose-100 text-rose-900 border-rose-200",
  reopened:   "bg-amber-100 text-amber-900 border-amber-200",
  recurring:  "bg-sky-100 text-sky-900 border-sky-200",
  normal:     "bg-slate-100 text-slate-700 border-slate-200",
};
const CLASS_LABEL = {
  persistent: "Persisten",
  reopened:   "Dibuka Kembali",
  recurring:  "Berulang",
  normal:     "Normal",
};

function hoursToHuman(h) {
  if (h === null || h === undefined) return "—";
  if (h < 1) return `${Math.round(h * 60)} menit`;
  if (h < 48) return `${h.toFixed(1)} jam`;
  return `${(h / 24).toFixed(1)} hari`;
}

export default function ExecutiveAlertAnalytics() {
  const [analytics, setAnalytics] = useState(null);
  const [threshold, setThreshold] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/executive/alert-analytics?months=12")
      .then(({ data }) => setAnalytics(data))
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses analitik alert eksekutif.");
        else setErr(formatApiError(e));
      });
    api.get("/executive/alert-threshold-review")
      .then(({ data }) => setThreshold(data))
      .catch(() => {});
  }, []);

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-alert-analytics-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-alert-analytics-no-access">
          {err}
        </div>
      </div>
    );
  }

  const series = analytics?.series || [];
  const recurrence = analytics?.recurrence || [];
  const thresholds = threshold?.items || [];

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exec-alert-analytics-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Activity className="h-6 w-6 text-sky-700" /> Analitik Alert Eksekutif
        </h1>
        <p className="text-xs md:text-sm text-slate-500">
          Tren alert 12 bulan, deteksi rekurensi, dan rekomendasi tinjauan threshold —
          <span className="font-semibold"> analisis historis, bukan perubahan otomatis</span>.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="alert-analytics-kpi">
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-[11px] text-slate-500">Total Lifecycle</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold" data-testid="kpi-total">{analytics?.total_alerts ?? 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-[11px] text-slate-500">Belum Terselesaikan</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold text-rose-700" data-testid="kpi-unresolved">{analytics?.unresolved_active_count ?? 0}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-[11px] text-slate-500 flex items-center gap-1"><Timer className="h-3 w-3" />Rata-rata Waktu Acknowledge</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold" data-testid="kpi-ack">{hoursToHuman(analytics?.avg_time_to_ack_hours)}</div></CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-1"><CardTitle className="text-[11px] text-slate-500 flex items-center gap-1"><Timer className="h-3 w-3" />Rata-rata Waktu Selesai</CardTitle></CardHeader>
          <CardContent><div className="text-2xl font-bold" data-testid="kpi-resolve">{hoursToHuman(analytics?.avg_time_to_resolve_hours)}</div></CardContent>
        </Card>
      </div>

      {/* Trend chart */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-700" />
            Tren Alert 12 Bulan ({analytics?.range?.from} → {analytics?.range?.to})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {series.length === 0 ? (
            <div className="py-10 text-center text-xs text-slate-400 italic" data-testid="alert-trend-empty">
              Data historis belum cukup
            </div>
          ) : (
            <div style={{ width: "100%", height: 260, minWidth: 320 }}>
              <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                <BarChart data={series}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="month" fontSize={11} />
                  <YAxis fontSize={11} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="CRITICAL" stackId="a" fill="#e11d48" name="CRITICAL" />
                  <Bar dataKey="WARNING" stackId="a" fill="#f59e0b" name="WARNING" />
                  <Bar dataKey="INFO" stackId="a" fill="#0ea5e9" name="INFO" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recurrence */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <Repeat className="h-4 w-4 text-sky-700" /> Deteksi Rekurensi Alert
          </CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kode Alert</TableHead>
                <TableHead>Klasifikasi</TableHead>
                <TableHead className="text-right">Total Lifecycle</TableHead>
                <TableHead className="text-right">Belum Selesai</TableHead>
                <TableHead>Bulan Terakhir Muncul</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recurrence.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-slate-400 italic py-6">
                  Belum ada riwayat alert
                </TableCell></TableRow>
              )}
              {recurrence.map((r) => (
                <TableRow key={r.alert_code} data-testid={`rec-row-${r.alert_code}`}>
                  <TableCell className="font-mono text-xs">{r.alert_code}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={CLASS_COLOR[r.classification] || CLASS_COLOR.normal}
                             data-testid={`rec-class-${r.alert_code}`}>
                      {CLASS_LABEL[r.classification] || "Normal"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{r.total_lifecycles}</TableCell>
                  <TableCell className="text-right">{r.unresolved_count}</TableCell>
                  <TableCell className="text-xs">{(r.months || []).slice(-3).join(", ") || "—"}</TableCell>
                  <TableCell>
                    <Badge variant={r.status_now === "ACTIVE" ? "destructive" : "outline"} className="text-[10px]">
                      {r.status_now}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Threshold Review */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm flex items-center gap-2">
            <History className="h-4 w-4 text-slate-600" /> Rekomendasi Tinjauan Threshold
          </CardTitle>
          <div className="text-[11px] text-slate-500">
            {threshold?.label || "Analisis historis — bukan perubahan otomatis"}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Alert</TableHead>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">Periode</TableHead>
                <TableHead className="text-right">Warning Saat Ini</TableHead>
                <TableHead className="text-right">Critical Saat Ini</TableHead>
                <TableHead className="text-right">Median</TableHead>
                <TableHead className="text-right">P75</TableHead>
                <TableHead className="text-right">P90</TableHead>
                <TableHead className="text-right">Min / Max</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {thresholds.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center text-xs text-slate-400 italic py-6">
                  Data historis belum cukup
                </TableCell></TableRow>
              )}
              {thresholds.map((t) => (
                <TableRow key={t.alert_code} data-testid={`thr-row-${t.alert_code}`}>
                  <TableCell className="font-mono text-xs">{t.alert_code}</TableCell>
                  <TableCell className="text-xs">{t.metric}</TableCell>
                  <TableCell className="text-right text-xs">
                    {t.periods_available}/{t.min_required}
                    {!t.available && <div className="text-[10px] text-amber-700">Data belum cukup</div>}
                  </TableCell>
                  <TableCell className="text-right">{t.current_warning ?? "—"}</TableCell>
                  <TableCell className="text-right">{t.current_critical ?? "—"}</TableCell>
                  <TableCell className="text-right">{t.median ?? "—"}</TableCell>
                  <TableCell className="text-right">{t.p75 ?? "—"}</TableCell>
                  <TableCell className="text-right">{t.p90 ?? "—"}</TableCell>
                  <TableCell className="text-right text-xs">
                    {t.observed_min ?? "—"} / {t.observed_max ?? "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="text-[11px] text-slate-500 mt-3 italic">
            Catatan: perubahan threshold dilakukan manual lewat halaman Konfigurasi Alert Eksekutif.
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
